import {AppShell} from '@mantine/core';
import React, {useEffect, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {RoleEnum, UserStatusEnum} from 'src/api/ProactApiClient';
import {InstanceBanner} from 'src/app/layout/PageLayout/InstanceBanner';
import {OfflineStatusBanner} from 'src/app/offline/OfflineStatusBanner';
import {useAppSelector} from 'src/redux/hooks';
import {useProjectScope} from 'src/shared/hooks/useProjectScope';
import {useTabsScrollAffordance} from 'src/shared/hooks/useTabsScrollAffordance';
import {GlobalSearch} from '../GlobalSearch/GlobalSearch';
import {Sidebar, SidebarRailWidth, SidebarWidth} from '../Sidebar/Sidebar';
import {ErrorFallback} from './components/ErrorFallback';
import {isPublicUrl} from './helpers';

import classes from './PageLayout.module.css';

export interface PageLayoutProps {
  children?: React.ReactNode;
}
export const PageLayout: React.FC<PageLayoutProps> = ({children}) => {
  const url = window.location.pathname;
  const [isPublicURL, setIsPublicURL] = useState(isPublicUrl(url));

  useEffect(() => {
    setIsPublicURL(isPublicUrl(url));
  }, [url]);

  const {session} = useAppSelector(state => state.session);
  const isHeadlessMode = useAppSelector(
    state => state.interface.isHeadlessMode
  );
  const isSidebarCollapsed = useAppSelector(
    state => state.interface.isSidebarCollapsed
  );

  const isUserBlocked =
    session == null ||
    session.user === null ||
    session.isCognitoUserMatched === false ||
    session.userStatus === UserStatusEnum.Inactive ||
    session.user?.roleId === RoleEnum.NotificationOnly;

  const [explode, setExplode] = React.useState(false);

  useTabsScrollAffordance();

  // Remount routed content when the sidebar project scope changes so local page
  // numbers, in-page filters and drawers reset; the sidebar itself stays mounted.
  const projectScope = useProjectScope();

  const showSidebar = !isPublicURL && !isUserBlocked && !isHeadlessMode;

  return (
    <AppShell
      navbar={{
        width: isSidebarCollapsed ? SidebarRailWidth : SidebarWidth,
        breakpoint: 'sm',
        collapsed: {mobile: true, desktop: !showSidebar},
      }}
      padding="md">
      {showSidebar && (
        <>
          <Sidebar />
          <GlobalSearch />
        </>
      )}
      <AppShell.Main className={classes.main}>
        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onReset={() => setExplode(false)}
          resetKeys={[explode]}>
          {showSidebar && <OfflineStatusBanner />}
          {showSidebar && <InstanceBanner />}
          <React.Fragment key={projectScope?.id ?? 'all'}>
            {children}
          </React.Fragment>
        </ErrorBoundary>
      </AppShell.Main>
    </AppShell>
  );
};
